import { z } from 'zod'
import { getEvaluationForms } from './evaluationFormManager.api'
import { CurrentFormState } from './efmTypes'
import { SCORE_TYPE } from './efmConstants'

export const zodValidateSave = async (currentForm: CurrentFormState): Promise<z.SafeParseSuccess<SavePercentFormSchema> | z.SafeParseError<SavePercentFormSchema>> => {
  const isPoints = currentForm.scoreType.id === SCORE_TYPE.POINTS
  const result = isPoints ? await SavePointFormSchema.safeParseAsync(currentForm) : await SavePercentFormSchema.safeParseAsync(currentForm)
  return result
}
export const zodValidateNewSave = async (currentForm: CurrentFormState): Promise<z.SafeParseSuccess<SaveNewPercentFormSchema> | z.SafeParseError<SaveNewPercentFormSchema>> => {
  const isPoints = currentForm.scoreType.id === SCORE_TYPE.POINTS
  const result = isPoints ? await SaveNewPointFormSchema.safeParseAsync(currentForm) : await SaveNewPercentFormSchema.safeParseAsync(currentForm)
  return result
}
export const zodEnableSave = async (currentForm: CurrentFormState): Promise<z.SafeParseSuccess<SaveDraft> | z.SafeParseError<SaveDraft>> => {
  const result = await SaveDraft.safeParseAsync(currentForm)
  return result
}
export const zodValidatePublish = async (currentForm: CurrentFormState | EvaluationFormSchema): Promise<z.SafeParseSuccess<PublishPointForm> | z.SafeParseError<PublishPointForm>> => {
  const isPoints = currentForm.scoreType.id === SCORE_TYPE.POINTS
  const result = isPoints ? await PublishPointForm.safeParseAsync(currentForm) : await PublishPercentForm.safeParseAsync(currentForm)
  return result
}

// QUESTIONS
const PointWeight = z.number().min(0).max(100).transform(() => 1)
const PercentWeight = z.number().min(0).max(100).transform(val => {
  if (val === 0) {
    return 0
  }
  return Number((val / 100.0).toFixed(6))
})

const delta = 0.0000001
const target = 1.0

// OPTION
const PublishPointOption = z.object({
  id: z.number(),
  type: z.enum(['ADDITIVE', 'KPI', 'NOTAPPLICABLE']),
  label: z.string().max(64).trim().min(1),
  isDefault: z.boolean(),
  kpiPriority: z.number().default(0),
  order: z.number().int(),
  points: z.number().int(),
  // TODO optional default null ???
  displaySection: z.union([z.number(), z.null()]).optional(),
  displayQuestion: z.union([z.number(), z.null()]).optional(),
})

const ValidatePointOptions = z.array(PublishPointOption).min(1, { message: 'questionOptionMinOne' }).superRefine((items, ctx) => {
  if (!items.some(item => item.isDefault === true)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'questionOptionDefault',
    })
  }
})
const ValidatePercentOptions = z.array(PublishPointOption).min(1, { message: 'questionOptionMinOne' }).superRefine((items, ctx) => {
  if (!items.some(item => item.points === 100)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'questionOptionMinOnehundred',
    })
  }
  if (!items.some(item => item.isDefault === true)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'questionOptionDefault',
    })
  }
})

// QUESTION
const PublishPointQuestion = z.object({
  id: z.number(),
  text: z.string().max(1000).trim().min(1),
  description: z.string().max(1000).trim().optional(),
  weight: PointWeight,
  order: z.number(),
  // TODO optional
  displayOnLoad: z.preprocess(val => {
    if (val === null) {
      return true
    } else {
      return val
    }
  }, z.boolean()),
  options: ValidatePointOptions,
})
const PublishPercentQuestion = PublishPointQuestion.extend({
  weight: PercentWeight,
  options: ValidatePercentOptions,
})

// SECTION
export const PublishPointSection = z.object({
  id: z.number(),
  name: z.string().max(64).trim().min(1),
  weight: PointWeight,
  order: z.number(),
  endColor: z.string().max(8).default('#ffffff'), // 7 length #ffffff?
  startColor: z.string().max(8).default('#000000'),
  // TODO optional
  displayOnLoad: z.preprocess(val => {
    if (val === null) {
      return true
    } else {
      return val
    }
  }, z.boolean()),
  questions: z.array(PublishPointQuestion).min(1, { message: 'sectionMinOneQuestion' }),
})
export const PublishPercentSection = PublishPointSection.extend({
  weight: PercentWeight,
  questions: z.array(PublishPercentQuestion).min(1, { message: 'sectionMinOneQuestion' }),
})

// FORM
const FormGeneric = z.object({
  approvalRequired: z.boolean(),
  bandRanges: z.array(z.object({
    band: z.number(),
    begin: z.number(),
    end: z.number(),
  })),
  created: z.number(),
  createdTz: z.string(),
  creator: z.string().max(64),
  defaultForm: z.boolean().default(false),
  description: z.string().max(128).trim().min(1), // not empty
  id: z.number(),
  manualWeighting: z.boolean(),
  name: z.string().max(64).trim().min(1), // not empty, also needs to be unique
  responseType: z.object({
    id: z.number().int().min(1).max(3), // 1 | 2 | 3
  }),
  scoreType: z.object({
    id: z.number().int().min(1).max(2), // 1 | 2
  }),
  status: z.number(),
  updated: z.number(),
  updatedTz: z.string(),
  updater: z.string(),
  // TODO optional
  hasBranchingLogic: z.preprocess(val => !!val, z.boolean()),
})
const NewFormGeneric = FormGeneric.omit({
  id: true,
})

const uniqueNameValidator = async (val: PublishGeneric) => {
  // get all eval forms
  const result = await getEvaluationForms()
    .then(response => {
      // filter and map all other form names to lower case
      const names = response
        .filter((form: { id: number | undefined }) => form.id !== val.id)
        .map((entry: { name: string }) => entry.name.toLowerCase())
      // check to see if name exists
      // returning any truthy value will pass validation
      if (names.includes(val.name.toLowerCase())) {
        return false
      } else {
        return true
      }
    })
    .catch(() => {
      return false
    })
  return result
}

const blValidationFunction = (val: PublishGeneric) => {
  if (!val.hasBranchingLogic) {
    return true
  }
  // for each option that has a displaySection || displayQuestion --> make sure that it exists in the form.
  const optionsWithDisplayValues = []
  const conditionalDisplayTags = []
  for (const section of val.sections) {
    const questions = section.questions
    if (!section.displayOnLoad) {
      conditionalDisplayTags.push({
        section: section.order,
        question: null,
      })
    }

    for (const question of questions) {
      const options = question.options
      if (!question.displayOnLoad) {
        conditionalDisplayTags.push({
          section: section.order,
          question: question.order,
        })
      }

      for (const option of options) {
        if (option.displayQuestion || option.displaySection) {
          optionsWithDisplayValues.push(
            {
              path: { section: section.order, question: question.order, option: option.order },
              displaySection: option.displaySection,
              displayQuestion: option.displayQuestion,
            },
          )
        }
      }
    }
  }
  // if there are no "display to" selections we are done validating? // do we need to check each always visible boolean?
  const emptyOptionsList = optionsWithDisplayValues.length === 0
  const emptyTagsList = conditionalDisplayTags.length === 0
  // if there are no conditional tags && no display selections we are done
  if (emptyTagsList && emptyOptionsList) {
    return true
  }
  // These next two states should never happen but we need to check for it.
  // Potential TODO to add a unique message for each of these states re superRefine
  if (emptyTagsList && !emptyOptionsList) {
    // There are some visible tags that should read conditional
    return false
  }
  if (!emptyTagsList && emptyOptionsList) {
    // there are conditional tags that should read as visible
    return false
  }
  // for each conditional tag we want to make sure an option is pointing to it
  const result = []
  for (const conditional of conditionalDisplayTags) {
    if (conditional.question) {
      result.push(optionsWithDisplayValues.some(x => x.displayQuestion === conditional.question && x.path.section === conditional.section))
    }
    if (!conditional.question) {
      result.push(optionsWithDisplayValues.some(x => x.displaySection === conditional.section))
    }
  }
  // if there is a single false, fail validation
  return !result.includes(false)
}

type PublishGeneric = z.infer<typeof PublishGeneric>
const PublishGeneric = FormGeneric.extend({
  sections: z.array(PublishPointSection).min(1, { message: 'pleaseAddSection' }).superRefine((sections, ctx) => {
    const uniqueNames = new Set(sections.map(section => section.name.toLowerCase()))
    if (uniqueNames.size < sections.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'uniqueSectionNameRequired',
      })
    }
  }),
})

export type PublishPointForm = z.infer<typeof PublishPointForm>
export const PublishPointForm = PublishGeneric.refine(async val => uniqueNameValidator(val), {
  message: 'uniqueNameRequired',
}).refine(val => blValidationFunction(val), { message: 'Please review branching logic selections' })

export type PublishPercentForm = z.infer<typeof PublishPercentForm>
export const PublishPercentForm = FormGeneric.extend({
  sections: z.array(PublishPercentSection).min(1, { message: 'pleaseAddSection' }).superRefine((sections, ctx) => {
    const uniqueNames = new Set(sections.map(section => section.name.toLowerCase()))
    if (uniqueNames.size < sections.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'uniqueSectionNameRequired',
      })
    }
  }),
}).refine(async val => uniqueNameValidator(val), {
  message: 'uniqueNameRequired',
}).refine(val => {
  if (!val.manualWeighting) {
    return true
  }
  let sectionSum = 0.0
  for (const section of val.sections) {
    sectionSum += section.weight
  }
  const result = ((sectionSum >= target - delta) && (sectionSum <= target + delta))
  return result
}, {
  message: 'sectionWeightOnehundred',
}).refine(val => {
  if (!val.manualWeighting) {
    return true
  }
  const checkEachSection: boolean[] = []
  for (const section of val.sections) {
    const questions = section.questions
    let questionSum = 0.0
    for (const question of questions) {
      questionSum += question.weight
    }
    // const isSectionBalanced = questionSum === 1
    const isSectionBalanced = ((questionSum >= target - delta) && (questionSum <= target + delta))
    checkEachSection.push(isSectionBalanced)
  }
  return !checkEachSection.includes(false)
}, {
  // TODO path
  message: 'questionWeightOnehundred',
}).refine(val => blValidationFunction(val), { message: 'Please review branching logic selections' })

// TODO decide on enable Publish criteria, until further notice will use SaveDraft schema.
export type EnablePublish = z.infer<typeof EnablePublish>
export const EnablePublish = FormGeneric.extend({
  sections: z.array(z.object({
    name: z.string().max(64).trim().min(1),
    questions: z.array(z.object({
      text: z.string().max(1000).trim().min(1),
      options: z.array(z.object({
        label: z.string().max(64).trim().min(1),
      })).min(1, { message: 'questionOptionMinOne' }),
    })).min(1, { message: 'sectionMinOneQuestion' }),
  })).min(1, { message: 'pleaseAddSection' }),
})

export type SaveDraft = z.infer<typeof SaveDraft>
export const SaveDraft = FormGeneric.extend({
  created: z.number().optional(),
  createdTz: z.string().optional(),
  creator: z.string().max(64).optional(),
  sections: z.array(z.object({
    name: z.string().max(64).trim(),
  })).min(1, { message: 'pleaseAddSection' }),
})

// Full Save Schema FOR POST
const SavePointOption = PublishPointOption.extend({
  label: z.string().max(64).trim().optional(),
})
// Questions
const SavePointQuestion = PublishPointQuestion.extend({
  text: z.string().max(1000).trim().optional(),
  weight: z.number().default(1).transform(() => 1),
  options: z.array(SavePointOption).optional(),
})
const SavePercentQuestion = SavePointQuestion.extend({
  weight: PercentWeight,
})
const SaveNewPointQuestion = SavePointQuestion.omit({
  id: true,
})
const SaveNewPercentQuestion = SavePercentQuestion.omit({
  id: true,
})
// Sections
const SavePointSection = PublishPointSection.extend({
  weight: z.number().default(1).transform(() => 1),
  questions: z.array(SavePointQuestion).optional(),
})
const SavePercentSection = SavePointSection.extend({
  weight: PercentWeight,
  questions: z.array(SavePercentQuestion).optional(),
})
const SaveNewPointSection = z.object({
  name: z.string().max(64).trim().min(1),
  weight: z.number().default(1).transform(() => 1),
  order: z.number(),
  endColor: z.string().max(8).default('#ffffff'), // 7 length #ffffff?
  startColor: z.string().max(8).default('#000000'),
  // TODO optional
  displayOnLoad: z.preprocess(val => {
    if (val === null) {
      return true
    } else {
      return val
    }
  }, z.boolean()),
  questions: z.array(SaveNewPointQuestion).optional(),
})
const SaveNewPercentSection = SaveNewPointSection.extend({
  weight: PercentWeight,
  questions: z.array(SaveNewPercentQuestion).optional(),
})

// Save EXPORTS
// SaveNewPoint, SaveNewPercent, SavePoint, SavePercent
export type SaveNewPercentFormSchema = z.infer<typeof SaveNewPercentFormSchema>
export const SaveNewPercentFormSchema = NewFormGeneric.extend({
  sections: z.array(SaveNewPercentSection).min(1, { message: 'pleaseAddSection' }).superRefine((sections, ctx) => {
    const uniqueNames = new Set(sections.map(section => section.name.toLowerCase()))
    if (sections.length > 1 && uniqueNames.size < sections.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'uniqueSectionNameRequired',
      })
    }
  }),
}).refine(async val => {
  const result = await getEvaluationForms()
    .then(response => {
      const names = response
        .map((entry: { name: string }) => entry.name.toLowerCase())
      if (names.includes(val.name.toLowerCase())) {
        return false
      } else {
        return true
      }
    })
    .catch(() => {
      return false
    })
  return result
}, {
  message: 'uniqueNameRequired',
})
export type SaveNewPointFormSchema = z.infer<typeof SaveNewPointFormSchema>
export const SaveNewPointFormSchema = NewFormGeneric.extend({
  sections: z.array(SaveNewPointSection).min(1, { message: 'minOneSection' }).superRefine((sections, ctx) => {
    const uniqueNames = new Set(sections.map(section => section.name.toLowerCase()))
    if (sections.length > 1 && uniqueNames.size < sections.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'uniqueSectionNameRequired',
      })
    }
  }),
}).refine(async val => {
  const result = await getEvaluationForms()
    .then(response => {
      const names = response
        .map((entry: { name: string }) => entry.name.toLowerCase())
      if (names.includes(val.name.toLowerCase())) {
        return false
      } else {
        return true
      }
    })
    .catch(() => {
      return false
    })
  return result
}, {
  message: 'uniqueNameRequired',
})

export type SavePercentFormSchema = z.infer<typeof SavePercentFormSchema>
export const SavePercentFormSchema = FormGeneric.extend({
  sections: z.array(SavePercentSection).min(1, { message: 'minOneSection' }).superRefine((sections, ctx) => {
    const uniqueNames = new Set(sections.map(section => section.name.toLowerCase()))
    if (sections.length > 1 && uniqueNames.size < sections.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'uniqueSectionNameRequired',
      })
    }
  }),
}).refine(async val => {
  const result = await getEvaluationForms()
    .then(response => {
      const names = response
        .filter((form: { id: number | undefined }) => form.id !== val.id)
        .map((entry: { name: string }) => entry.name.toLowerCase())
      if (names.includes(val.name.toLowerCase())) {
        return false
      } else {
        return true
      }
    })
    .catch(() => {
      return false
    })
  return result
}, {
  message: 'uniqueNameRequired',
})

export type SavePointFormSchema = z.infer<typeof SavePointFormSchema>
export const SavePointFormSchema = FormGeneric.extend({
  sections: z.array(SavePointSection).min(1, { message: 'minOneSection' }).superRefine((sections, ctx) => {
    const uniqueNames = new Set(sections.map(section => section.name.toLowerCase()))
    if (sections.length > 1 && uniqueNames.size < sections.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'uniqueSectionNameRequired',
      })
    }
  }),
}).refine(async val => {
  const result = await getEvaluationForms()
    .then(response => {
      const names = response
        .filter((form: { id: number | undefined }) => form.id !== val.id)
        .map((entry: { name: string }) => entry.name.toLowerCase())
      if (names.includes(val.name.toLowerCase())) {
        return false
      } else {
        return true
      }
    })
    .catch(() => {
      return false
    })
  return result
}, {
  message: 'uniqueNameRequired',
})

const cleanValue = (num: number) => {
  const noDecimalVal = Number(String(num).replace(/[^0-9]/g, ''))
  const numberOfDigits = noDecimalVal.toString().length
  if (numberOfDigits > 6) {
    return Number(num.toFixed(4))
  }
  return num
}

// THIS IS FOR GET REQUEST
export type EvaluationFormSchema = z.infer<typeof EvaluationFormSchema>
export const EvaluationFormSchema = z.object({
  approvalRequired: z.boolean(),
  bandRanges: z.array(z.object({
    band: z.number(),
    begin: z.number(),
    end: z.number(),
  })).transform(val => {
    if (!val[0] || !val[1]) {
      return val
    }
    if (val[0].begin === val[1].end) {
      return [
        { ...val[0], end: val[0].begin },
        { ...val[1], begin: val[1].end },
        val[2],
      ]
    }
    return val
  }),
  hasBranchingLogic: z.preprocess(val => !!val, z.boolean()),
  created: z.number(),
  createdTz: z.string(),
  creator: z.string(),
  defaultForm: z.boolean().default(false),
  description: z.string().min(1).max(128),
  id: z.number(),
  manualWeighting: z.boolean(),
  name: z.string(),
  responseType: z.object({
    id: z.number().int().min(1).max(3),
  }),
  scoreType: z.object({
    id: z.number().int().min(1).max(2),
  }),
  sections: z.array(z.object({
    id: z.number(),
    name: z.string(),
    weight: z.number().max(1).min(0).transform(val => val * 100),
    order: z.number(),
    endColor: z.string().max(8).optional(),
    startColor: z.string().max(8).optional(),
    displayOnLoad: z.preprocess(val => {
      if (val === null) {
        return true
      } else {
        return val
      }
    }, z.boolean()),
    questions: z.array(z.object({
      id: z.number(),
      text: z.string(),
      description: z.string(),
      weight: z.number().max(1).min(0).transform(val => val * 100),
      order: z.number(),
      displayOnLoad: z.preprocess(val => {
        if (val === null) {
          return true
        } else {
          return val
        }
      }, z.boolean()),
      options: z.array(z.object({
        id: z.number(),
        type: z.enum(['ADDITIVE', 'KPI', 'NOTAPPLICABLE']),
        label: z.string().optional(),
        isDefault: z.boolean(),
        kpiPriority: z.number().default(0),
        order: z.number().int(),
        points: z.number().int(),
        // TODO how will we represent defaults
        // numbers to represent ORDINAL
        displaySection: z.union([z.number(), z.null()]).default(null),
        displayQuestion: z.union([z.number(), z.null()]).default(null),
      }),
      ).optional(),
    })).optional(),
  })).optional(),
  status: z.number(),
  updated: z.number(),
  updatedTz: z.string(),
  updater: z.string(),
}).transform(val => {
  if (val.scoreType.id === SCORE_TYPE.POINTS) {
    return val
  }
  if (val.sections?.length === 0) {
    return val
  }
  const updatedSections = val.sections?.map(section => {
    const updatedQuestions = section.questions?.map(question => {
      return { ...question, weight: cleanValue(question.weight) }
    })
    return { ...section, weight: cleanValue(section.weight), questions: updatedQuestions }
  })
  return { ...val, sections: updatedSections }
})
